/**
 * Small shared UI primitives for the notification console screens — matched to
 * the previous console's classes (statpill, banner, page-header, empty, btn).
 */
import React from 'react';
import { View, Text, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import Icon, { IconName } from './Icon';
import { useTheme } from '../../context/themeContext';

type Tone = 'neutral' | 'success' | 'error' | 'warning' | 'info';

const TONE: Record<Tone, string> = {
  neutral: '#6B748A', success: '#2D8A5F', error: '#C94444', warning: '#B87D0A', info: '#3B6FD5',
};

export const StatPill: React.FC<{ label: string; value: number | string; tone?: Tone }> = ({ label, value, tone = 'neutral' }) => {
  const { theme: { colors, font } } = useTheme();
  const c = TONE[tone];
  return (
    <View style={[u.pill, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <View style={[u.pillDot, { backgroundColor: c }]} />
      <Text style={[u.pillLabel, { color: colors.ink3 }]}>{label}</Text>
      <Text style={[u.pillValue, { color: colors.ink, fontFamily: font.mono }]}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Text>
    </View>
  );
};

export const Banner: React.FC<{ tone: Tone; icon?: IconName; children: React.ReactNode; onClose?: () => void }> = ({ tone, icon, children, onClose }) => {
  const c = TONE[tone];
  return (
    <View style={[u.banner, { backgroundColor: c + '14', borderColor: c + '44' }]}>
      {icon && <Icon name={icon} size={16} color={c} />}
      <View style={{ flex: 1 }}>{children}</View>
      {onClose && <Pressable onPress={onClose} hitSlop={8}><Icon name="x" size={14} color={c} /></Pressable>}
    </View>
  );
};

export const PageHeader: React.FC<{ title: string; subtitle?: string; right?: React.ReactNode }> = ({ title, subtitle, right }) => {
  const { theme: { colors } } = useTheme();
  return (
    <View style={u.header}>
      <View style={{ flex: 1 }}>
        <Text style={[u.title, { color: colors.ink }]}>{title}</Text>
        {!!subtitle && <Text style={[u.subtitle, { color: colors.ink3 }]}>{subtitle}</Text>}
      </View>
      {right}
    </View>
  );
};

export const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme: { colors } } = useTheme();
  return <Text style={[u.sectionLabel, { color: colors.ink4 }]}>{children}</Text>;
};

export const EmptyState: React.FC<{ icon?: IconName; title: string; body?: string; action?: React.ReactNode }> = ({ icon, title, body, action }) => {
  const { theme: { colors } } = useTheme();
  return (
    <View style={u.empty}>
      {icon && <Icon name={icon} size={44} stroke={1.5} color={colors.ink4} />}
      <Text style={[u.emptyTitle, { color: colors.ink }]}>{title}</Text>
      {!!body && <Text style={[u.emptyBody, { color: colors.ink3 }]}>{body}</Text>}
      {action}
    </View>
  );
};

export const Btn: React.FC<{
  label?: string; icon?: IconName; onPress?: () => void; kind?: 'primary' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg'; disabled?: boolean; loading?: boolean; block?: boolean; children?: React.ReactNode;
}> = ({ label, icon, onPress, kind = 'ghost', size = 'md', disabled, loading, block, children }) => {
  const { theme: { colors } } = useTheme();
  const pad = size === 'sm' ? { paddingVertical: 6, paddingHorizontal: 10 } : size === 'lg' ? { paddingVertical: 12, paddingHorizontal: 18 } : { paddingVertical: 9, paddingHorizontal: 14 };
  const bg = kind === 'primary' ? colors.accent : kind === 'outline' ? 'transparent' : 'transparent';
  const border = kind === 'outline' ? colors.lineStrong : 'transparent';
  const fg = kind === 'primary' ? '#fff' : colors.ink2;
  return (
    <Pressable
      onPress={disabled || loading ? undefined : onPress}
      style={[u.btn, pad, { backgroundColor: bg, borderColor: border, borderWidth: kind === 'outline' ? 1 : 0, opacity: disabled ? 0.5 : 1 }, block && { flex: 1, justifyContent: 'center' }]}
    >
      {loading ? <ActivityIndicator size="small" color={fg} /> : icon && <Icon name={icon} size={size === 'sm' ? 12 : 14} color={fg} />}
      {(label || children) && <Text style={[u.btnText, { color: fg, fontSize: size === 'sm' ? 12 : 13 }]}>{label}{children}</Text>}
    </Pressable>
  );
};

const u = StyleSheet.create({
  pill: { flexDirection: 'row', alignItems: 'center', gap: 7, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 2, borderWidth: 1 },
  pillDot: { width: 7, height: 7, borderRadius: 4 },
  pillLabel: { fontSize: 12, fontWeight: '500' },
  pillValue: { fontSize: 13, fontWeight: '700' },
  banner: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, padding: 12, borderRadius: 2, borderWidth: 1 },
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
  title: { fontSize: 22, fontWeight: '700' },
  subtitle: { fontSize: 13, marginTop: 3 },
  sectionLabel: { fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10, marginTop: 4 },
  empty: { alignItems: 'center', justifyContent: 'center', padding: 40, gap: 8 },
  emptyTitle: { fontSize: 15, fontWeight: '600' },
  emptyBody: { fontSize: 13, textAlign: 'center', maxWidth: 380 },
  btn: { flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 2 },
  btnText: { fontWeight: '500' },
});
